// 2026-05-26 - FormuLearning - Step 6 카드 컨테이너
// 2026-09-23 수정 - 이슈 #83: PK 시뮬레이션 요청 동안 진행 중 작업 잠금(`busyStore`) 등록 — Break 없음.
// 2026-09-24 수정 - 이슈 #63: PK 재설계 — 코호트·실측 비교를 걷어내고 국민대 아티팩트 기준
//   Reference vs Test 흡수 시뮬레이션(입력 4군 · 고급 설정 · 용출 편집 · 결과·내보내기)으로 교체.
//   입력 편집 저장은 `useDeferredSave`(Autosave ON=확정마다 PUT, OFF=Save and continue 때 일괄).
// 파일 위치: frontend/src/features/step6/Step6Card.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 Run Simulation·AI Summary 생성 비활성.

import { useCallback, useEffect, useState } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { useSummaryStore } from '../../stores/summaryStore'
import { useBusyStore } from '../../stores/busyStore'
import { useDeferredSave } from '../../hooks/useDeferredSave'
import { errorDetail, generateAiSummary, getStep6, runSimulation, updateStep6 } from '../../api/step6'
import type { PkInputs, SimulationResult, Step6View } from '../../api/step6'
import InputsSection from './InputsSection'
import DissolutionSection from './DissolutionSection'
import ResultSection from './ResultSection'
import { NumField } from './fields'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

const saveView = (pid: string, patch: Partial<Step6View>) => updateStep6(pid, patch.inputs!)

export default function Step6Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  const [view, setView] = useState<Step6View | null>(null)
  const [simulating, setSimulating] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [generatingSummary, setGeneratingSummary] = useState(false)
  const setSummary = useSummaryStore((s) => s.setSummary)
  const canWrite = useCanWrite()
  const { apply } = useDeferredSave<Step6View>({ step: 6, projectId, data: view, setData: setView, save: saveView })

  // 2026-07-18 - 이슈 #40: Step6 영속 AI Summary(버튼 생성)를 발행. 표시는 CardShell 위젯이 전담.
  useEffect(() => {
    setSummary(6, view?.ai_summary, view?.ai_summary_ko)
  }, [view?.ai_summary, view?.ai_summary_ko, setSummary])

  useEffect(() => {
    if (!projectId) return
    getStep6(projectId).then(setView)
  }, [projectId])

  const onChange = useCallback((inputs: PkInputs) => { apply({ inputs }) }, [apply])

  const handleSimulate = useCallback(async () => {
    if (!projectId || !view) return
    setSimulating(true)
    setRunError(null)
    try {
      setView(await runSimulation(projectId, view.inputs))
    } catch (e) {
      setRunError(errorDetail(e))
    } finally {
      setSimulating(false)
    }
  }, [projectId, view])

  // 2026-09-23 - 이슈 #83: 요청 동안 진행 중 작업 잠금(#82)에 등록한다 — 다른 스텝·프로젝트 전환을 잠근다.
  // 중단 수단은 두지 않는다: 서버가 한 요청 안에서 계산·저장해 끊어도 저장되므로 Break 의 뜻과 어긋난다(검토 K3).
  useEffect(() => {
    if (!simulating) return
    const id = crypto.randomUUID()
    useBusyStore.getState().begin({ id, step: 6, label: 'Step 6 PK 시뮬레이션' })
    return () => useBusyStore.getState().end(id)
  }, [simulating])

  const handleGenerateSummary = useCallback(async () => {
    if (!projectId) return
    setGeneratingSummary(true)
    try {
      await generateAiSummary(projectId)
      setView(await getStep6(projectId))
    } finally {
      setGeneratingSummary(false)
    }
  }, [projectId])

  if (!view || !projectId) return <p className="text-xs text-text-secondary">Loading...</p>

  const inputs = view.inputs
  const result = (view.simulation_result && 'ref' in view.simulation_result) ? view.simulation_result as SimulationResult : null

  return (
    <div>
      <InputsSection projectId={projectId} view={view} inputs={inputs} onChange={onChange} />
      <DissolutionSection projectId={projectId} view={view} inputs={inputs} onChange={onChange} />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <button
          type="button"
          onClick={handleSimulate}
          disabled={simulating || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          className="rounded px-4 py-1.5 text-[11px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: 'var(--btn-accent-teal)', color: 'var(--color-bg-base)' }}
        >
          {simulating ? '계산 중…' : 'Run Simulation'}
        </button>
        <div className="w-40">
          <NumField label="AUC calculation period" unit="h" value={inputs.sim_time_h}
            onCommit={(v) => onChange({ ...inputs, sim_time_h: v ?? 72 })} />
        </div>
        {runError && <p className="text-[11px]" style={{ color: 'var(--color-danger)' }}>{runError}</p>}
      </div>

      {result
        ? <ResultSection projectId={projectId} result={result} outdated={view.result_outdated} />
        : <p className="mb-4 text-[11px] text-text-secondary">입력을 확인하고 Run Simulation 을 누르면 Reference·Test 혈중농도 곡선이 나옵니다.</p>}

      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">AI Summary</h4>
          <button
            onClick={handleGenerateSummary}
            disabled={generatingSummary || !result || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className="rounded px-3 py-1.5 text-[11px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ border: '1px solid var(--border-teal)', color: 'var(--color-accent-teal)' }}
          >
            {generatingSummary ? 'Generating...' : view.ai_summary ? 'Regenerate Summary' : 'Generate AI Summary'}
          </button>
        </div>
        {/* 2026-07-18 - 이슈 #40: 요약 표시는 카드 상단 공통 위젯(CardShell)이 전담. 여기선 생성 버튼과 안내만. */}
        {!view.ai_summary && (
          <p className="text-[11px] text-text-secondary">Run simulation first, then generate AI summary.</p>
        )}
      </div>
    </div>
  )
}
